import { useContext, useRef, useState } from 'react';
import {
  ChevronIcon,
  FileTextIcon,
  LightBulbIcon,
  MicIcon,
  PencilIcon,
  PlayOutlineIcon,
  PlayIcon,
  PlusIcon,
  RecordIcon,
  RecordingIcon,
  ThreeDots,
  TrashIcon,
  VideoIcon,
} from 'assets/icons';
import { AlbumContentType, NotificationType } from 'common/enums';
import { ButtonStyle } from 'common/button';
import { DropDownListItem } from 'common/drop-down-list-item';
import { Menu, PositionType } from 'common/menu';
import { ModalWarning } from 'common/modal-warning';
import { appActions, AppContext } from 'context/app-context';
import { adaptContentType, classnames } from 'helpers/utils';
import { useClickOutside } from 'hooks/click-outside';
import { NotificationObject } from 'models/notificationObject';
import { VaultsController } from 'networking/controllers/vaults-controller';
import { AlbumContentSerialized } from 'networking/types/album-detail';
import { RecordMode, RespondMenu, RespondMode } from 'common/respond-menu';
import { PromptFileChooser } from 'common/upload-modal';
import { RecordNow } from 'pages/vault-detail/components/tabs/planner/components/record-now';
import { VideoCallWaitingRoom } from 'pages/vault-detail/components/video-call-waiting-room';
import { SuggestedPromptsModal } from '../suggested-prompts-modal';
import styles from './album-planner.module.scss';

interface AlbumPlannerProps {
  vault: VaultType;
  albumId: number;
  // Shown in Record now as where answers are saved.
  albumName: string;
  prompts: AlbumContentSerialized[];
  answered: AlbumContentSerialized[];
  onAddEntry: () => void;
  onViewMode: (contentId?: number) => void;
  onContentsChanged: () => void;
}

const rowThumb = (content: AlbumContentSerialized) => {
  const type = adaptContentType(content);
  if (content.thumbnailUrl) {
    return (
      <span className={styles.rowThumb}>
        <img src={content.thumbnailUrl} alt="" loading="lazy" />
        {type === AlbumContentType.video && (
          <PlayIcon className={styles.rowThumbPlay} />
        )}
      </span>
    );
  }
  const icon = () => {
    switch (type) {
      case AlbumContentType.audio:
        return <RecordingIcon />;
      case AlbumContentType.write:
        return <PencilIcon />;
      default:
        return <FileTextIcon />;
    }
  };
  return (
    <span className={classnames(styles.rowThumb, styles.rowThumbFallback)}>
      {icon()}
    </span>
  );
};

interface PlannerRowProps {
  content: AlbumContentSerialized;
  isPrompt: boolean;
  // A prompt opens the "Respond with" menu below its row; an answered entry
  // opens in view mode.
  onOpen: (anchor: DOMRect) => void;
  onDelete: () => void;
}

const PlannerRow = ({
  content,
  isPrompt,
  onOpen,
  onDelete,
}: PlannerRowProps) => {
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const rowRef = useRef<HTMLDivElement>(null);
  useClickOutside(menuRef, () => setShowMenu(false));
  const open = () => {
    const row = rowRef.current;
    if (row) onOpen(row.getBoundingClientRect());
  };

  return (
    <div className={classnames(styles.row, 'gs-stagger')} ref={rowRef}>
      <button
        type="button"
        className={styles.rowMain}
        onClick={open}
        aria-haspopup={isPrompt ? 'menu' : undefined}
      >
        {isPrompt ? (
          <span className={styles.promptIcon}>
            <LightBulbIcon />
          </span>
        ) : (
          rowThumb(content)
        )}
        <span className={styles.rowTitle}>{content.name || 'Untitled'}</span>
      </button>
      <div className={styles.rowMenuWrap} ref={menuRef}>
        <button
          type="button"
          className={styles.rowMenuButton}
          aria-label="Entry options"
          onClick={() => setShowMenu((prev) => !prev)}
        >
          <ThreeDots />
        </button>
        {showMenu && (
          <Menu position={PositionType.BottomRight} className={styles.rowMenu}>
            <DropDownListItem
              leftIcon={isPrompt ? <RecordIcon /> : <PlayOutlineIcon />}
              onClick={() => {
                setShowMenu(false);
                open();
              }}
            >
              {isPrompt ? 'Answer prompt' : 'Open'}
            </DropDownListItem>
            <DropDownListItem
              leftIcon={<TrashIcon />}
              onClick={() => {
                setShowMenu(false);
                onDelete();
              }}
            >
              Delete
            </DropDownListItem>
          </Menu>
        )}
      </div>
    </div>
  );
};

export const AlbumPlanner = ({
  vault,
  albumId,
  albumName,
  prompts,
  answered,
  onAddEntry,
  onViewMode,
  onContentsChanged,
}: AlbumPlannerProps) => {
  const { dispatch } = useContext(AppContext);
  const [showRecordMenu, setShowRecordMenu] = useState(false);
  // Record now: from the Record menu (video), or answering a prompt the way
  // the user picked (journal, audio or video), starting on that prompt.
  const [recordNow, setRecordNow] = useState<{
    mode: RecordMode;
    topicId?: number;
  } | null>(null);
  // Upload, to answer a prompt: it's removed once the import adds memories.
  const [uploadPromptId, setUploadPromptId] = useState<number | null>(null);
  const [respondMenu, setRespondMenu] = useState<{
    content: AlbumContentSerialized;
    anchor: DOMRect;
  } | null>(null);
  const [showVideoCall, setShowVideoCall] = useState(false);
  const [showSuggestedPrompts, setShowSuggestedPrompts] = useState(false);
  const [contentToDelete, setContentToDelete] =
    useState<AlbumContentSerialized | null>(null);
  const recordMenuRef = useRef<HTMLDivElement>(null);
  useClickOutside(recordMenuRef, () => setShowRecordMenu(false));

  const notify = (title: string, type: NotificationType) =>
    dispatch({
      type: appActions.NOTIFICATION,
      notification: new NotificationObject({ show: true, title, type }),
    });

  const handleDelete = async () => {
    if (!contentToDelete) return;
    const target = contentToDelete;
    setContentToDelete(null);
    try {
      await VaultsController.deleteAlbumContent(
        target.id,
        target.fileSize ?? 0,
      );
      notify('Entry deleted', NotificationType.Success);
      onContentsChanged();
    } catch (error) {
      notify('Failed to delete entry', NotificationType.Error);
    }
  };

  return (
    <div className={styles.columns}>
      <section className={classnames(styles.column, 'gs-stagger')}>
        <header className={styles.columnHeader}>
          <span className={styles.columnTitle}>Prompts</span>
          <div className={styles.columnActions}>
            <div className={styles.recordWrap} ref={recordMenuRef}>
              <button
                type="button"
                className={styles.headerButton}
                onClick={() => setShowRecordMenu((prev) => !prev)}
              >
                <RecordIcon />
                <span>Record</span>
                <ChevronIcon
                  className={classnames(styles.chevron, {
                    [styles.chevronOpen]: showRecordMenu,
                  })}
                />
              </button>
              {showRecordMenu && (
                <Menu
                  position={PositionType.BottomRight}
                  className={styles.recordMenu}
                >
                  <DropDownListItem
                    leftIcon={<RecordIcon />}
                    onClick={() => {
                      setShowRecordMenu(false);
                      setRecordNow({ mode: 'video' });
                    }}
                  >
                    Record now
                  </DropDownListItem>
                  <DropDownListItem
                    leftIcon={<VideoIcon />}
                    onClick={() => {
                      setShowRecordMenu(false);
                      setShowVideoCall(true);
                    }}
                  >
                    Start video call
                  </DropDownListItem>
                </Menu>
              )}
            </div>
            <button
              type="button"
              className={styles.headerButton}
              onClick={() => setShowVideoCall(true)}
            >
              <span>Start Interview</span>
              <MicIcon />
            </button>
          </div>
        </header>
        <div className={styles.items}>
          {/* One modal: type a custom question, or pick from the pre-defined
              topics (Heritage, Growing Up…) or the user's interview templates. */}
          <button
            type="button"
            className={styles.addNew}
            onClick={() => setShowSuggestedPrompts(true)}
          >
            <PlusIcon />
            <span>Add new prompt</span>
          </button>
          {prompts.length === 0 && (
            <p className={styles.emptyHint}>
              No prompts yet. Add your own, or pick from our topics to plan what
              to record next.
            </p>
          )}
          {prompts.map((content) => (
            <PlannerRow
              key={content.id}
              content={content}
              isPrompt
              onOpen={(anchor) => setRespondMenu({ content, anchor })}
              onDelete={() => setContentToDelete(content)}
            />
          ))}
        </div>
      </section>

      <section className={classnames(styles.column, 'gs-stagger')}>
        <header className={styles.columnHeader}>
          <span className={styles.columnTitle}>Answered</span>
          <button
            type="button"
            className={styles.headerButton}
            onClick={() => onViewMode()}
          >
            <span>View Mode</span>
            <PlayOutlineIcon />
          </button>
        </header>
        <div className={styles.items}>
          <button type="button" className={styles.addNew} onClick={onAddEntry}>
            <PlusIcon />
            <span>Add new</span>
          </button>
          {answered.length === 0 && (
            <p className={styles.emptyHint}>
              Answered entries will show up here.
            </p>
          )}
          {answered.map((content) => (
            <PlannerRow
              key={content.id}
              content={content}
              isPrompt={false}
              onOpen={() => onViewMode(content.id)}
              onDelete={() => setContentToDelete(content)}
            />
          ))}
        </div>
      </section>

      {respondMenu && (
        <RespondMenu
          anchor={respondMenu.anchor}
          onClose={() => setRespondMenu(null)}
          onPick={(mode: RespondMode) => {
            if (mode === 'upload') setUploadPromptId(respondMenu.content.id);
            else if (mode === 'videocall') setShowVideoCall(true);
            else setRecordNow({ mode, topicId: respondMenu.content.id });
          }}
        />
      )}
      {recordNow && (
        <RecordNow
          vaultId={vault.id}
          albumId={albumId}
          albumName={albumName}
          topics={prompts}
          mode={recordNow.mode}
          initialTopicId={recordNow.topicId}
          vault={vault}
          answered={answered}
          onTopicsChanged={onContentsChanged}
          onClose={() => setRecordNow(null)}
        />
      )}
      {uploadPromptId !== null && (
        <PromptFileChooser
          vaultId={vault.id}
          albumId={albumId}
          promptId={uploadPromptId}
          onClose={() => setUploadPromptId(null)}
        />
      )}
      {showSuggestedPrompts && (
        <SuggestedPromptsModal
          vaultId={vault.id}
          albumId={albumId}
          existingPromptNames={prompts.map((p) => p.name || '')}
          onClose={() => setShowSuggestedPrompts(false)}
          onAdded={onContentsChanged}
        />
      )}
      {showVideoCall && (
        <VideoCallWaitingRoom
          vault={vault}
          albumId={albumId}
          onClose={() => setShowVideoCall(false)}
        />
      )}
      {contentToDelete && (
        <ModalWarning
          title="Delete entry"
          content="Are you sure you want to delete this Entry? This action cannot be undone."
          successButtonText="Delete"
          successStyleButton={ButtonStyle.DestructivePrimary}
          closeFn={() => setContentToDelete(null)}
          successFn={handleDelete}
        />
      )}
    </div>
  );
};
